import React, { useState } from 'react';
import StepSidebar from './StepSidebar';
import LedInfoModal from '../configFlowSteps/LedInfoModal';
import { WebStepKey, WEB_STEP_NUMBER, WEB_STEP_PHASE, WEB_STEPS } from './types';

interface ActionButton {
  label: string;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
  loadingLabel?: string;
}

interface WebStepShellProps {
  step: WebStepKey;
  /** Header back arrow handler (omit to hide the arrow). */
  onHeaderBack?: () => void;
  /** "Cancel Setup" button under the sidebar. */
  onCancel: () => void;
  primary?: ActionButton;
  secondary?: ActionButton;
  hideFooter?: boolean;
  /** Left illustration column content. */
  media?: React.ReactNode;
  children: React.ReactNode;
}

const WebStepShell: React.FC<WebStepShellProps> = ({
  step,
  onHeaderBack,
  onCancel,
  primary,
  secondary,
  hideFooter = false,
  media,
  children,
}) => {
  const [showLedInfo, setShowLedInfo] = useState(false);
  const fillPct = Math.round((WEB_STEP_NUMBER[step] / WEB_STEPS.length) * 100);
  const activePhase = WEB_STEP_PHASE[step];

  return (
    <div className="dcfw-overlay">
      <div className="dcfw-modal zoomOutLevel">
        <div className="dcfw-header">
          {onHeaderBack && (
            <button
              type="button"
              onClick={onHeaderBack}
              className="dcfw-header__back"
              aria-label="Back"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
              </svg>
            </button>
          )}
          <h2 className="device-title">Device Setup</h2>
        </div>

        <div className="dcfw-body">
          <aside className="dcfw-sidebar">
            <StepSidebar activePhase={activePhase} />
            <button type="button" className="dcfw-cancel" onClick={onCancel}>
              Cancel Setup
            </button>
          </aside>

          <section className="dcfw-main">
            <div className="dcfw-progress">
              <div
                className="dcfw-progress__fill"
                style={{ width: `${fillPct}%` }}
                aria-hidden="true"
              />
            </div>

            <div className="dcfw-main-row">
              <div className="dcfw-media">{media}</div>
              <div className="dcfw-content" style={{ position: 'relative' }}>
                <button
                  type="button"
                  className="dcf-step-title__info-btn"
                  onClick={() => setShowLedInfo(true)}
                  aria-label="LED indicator info"
                  style={{ position: 'absolute', top: 0, right: 0 }}
                >
                  <span className="dcf-step-title__info-icon">i</span>
                </button>
                {children}
              </div>
            </div>

            {!hideFooter && (primary || secondary) && (
              <div className="dcfw-actions">
                {secondary && (
                  <button
                    type="button"
                    onClick={secondary.onClick}
                    className="dcf-button dcf-button--secondary"
                    disabled={secondary.disabled || primary?.loading}
                  >
                    {secondary.label}
                  </button>
                )}
                {primary && (
                  <button
                    type="button"
                    onClick={primary.onClick}
                    className="dcf-button dcf-button--primary"
                    disabled={primary.disabled || primary.loading}
                  >
                    {primary.loading ? (
                      <>
                        <span className="dcf-spinner" aria-hidden="true" />
                        {primary.loadingLabel ?? 'Working…'}
                      </>
                    ) : (
                      primary.label
                    )}
                  </button>
                )}
              </div>
            )}
          </section>
        </div>
      </div>
      <LedInfoModal isOpen={showLedInfo} onClose={() => setShowLedInfo(false)} />
    </div>
  );
};

export default WebStepShell;
